/**
 * Car against car — the rigid-body contact, the bump, and the demolition.
 *
 * Before this file existed two cars passed through each other unless the
 * contact happened to be a demolition: ./demo detected and applied demos and
 * nothing else, so a non-demo contact did literally nothing. Cars overlapped,
 * slid through, and BUMP_VEL_GROUND_CURVE and friends sat in ./constants with
 * no reader.
 *
 * ===========================================================================
 * THE SOURCE
 * ===========================================================================
 *
 * All of the decision logic below is RocketSim's
 * `Arena::_BtCallback_OnCarCarCollision`, which Bullet invokes per manifold
 * point. Verbatim structure, with the four things it is easy to get wrong
 * called out where they happen:
 *
 *     for (int i = 0; i < 2; i++) {                 // BOTH WAYS
 *         bool isSwapped = (i == 1);
 *         if (isSwapped) std::swap(car1, car2);
 *         if (state.isDemoed || otherState.isDemoed) return;      // RETURN
 *         if ((state.carContact.otherCarID == car2->id)
 *             && (state.carContact.cooldownTimer > 0)) continue;  // PER VICTIM
 *         ...
 *         bool groundHit = car2->_internalState.isOnGround;       // THE VICTIM'S
 *         Vec bumpImpulse = velDir * baseScale
 *             + hitUpDir * BUMP_UPWARD_VEL_AMOUNT_CURVE.GetOutput(speed)
 *               * bumpForceScale;                                 // UPWARD ONLY
 *         car2->_velocityImpulseCache += bumpImpulse * UU_TO_BT;
 *     }
 *
 *   - BOTH WAYS. The loop swaps the pair and runs again, so on one tick A can
 *     bump B and B can bump A. A head-on between two ordinary cars pushes both.
 *
 *   - `return`, NOT `continue`, on a demolished car. If the first direction
 *     demolishes the victim, the second direction does not run at all: the car
 *     that just died cannot bump its killer back on the way out, and — the case
 *     the old comment in ./demo got wrong — a head-on between two SUPERSONIC
 *     cars kills exactly one of them, whichever the pair order reaches first.
 *
 *   - THE COOLDOWN IS PER ATTACKER, KEYED TO A SPECIFIC VICTIM. One slot on the
 *     attacker holding (otherCarID, cooldownTimer). It is not global and not
 *     pair-symmetric: while A cools down against B it may still bump C
 *     immediately, and B may bump A back immediately. See `carContact` on
 *     ./car-state.
 *
 *   - THE GROUND/AIR CURVE IS CHOSEN BY THE VICTIM. `car2->...->isOnGround`,
 *     not the attacker's. A grounded car takes the (weaker) ground curve however
 *     the attacker arrived, and the airborne curve is ~26 % stronger because
 *     nothing is holding the victim down.
 *
 *   - `bumpForceScale` MULTIPLIES ONLY THE UPWARD TERM. `a * b + c * d * e`
 *     groups as `(a*b) + ((c*d)*e)`; the forward term is untouched by it. This
 *     reads like a bug in the source and is reproduced as written, because a
 *     mutator that scales half a vector is still what the game does. It is 1 in
 *     the default game, so it changes nothing unless a caller passes otherwise.
 *
 * The one thing that is NOT verbatim is where the bumper test's contact point
 * comes from: RocketSim reads Bullet's manifold point, we use ./demo's
 * `contactPointLocal`. See the note there — our manifold is an unclipped support
 * set and would call a T-bone a bumper hit.
 *
 * ===========================================================================
 * THE PHYSICAL RESPONSE
 * ===========================================================================
 *
 * RocketSim does not write one: it overrides the manifold's friction and
 * restitution with CARCAR_COLLISION_FRICTION / _RESTITUTION and lets Bullet's
 * solver do the rest. So this file is that solver, and it is deliberately the
 * SAME one ./car-arena runs against the arena mesh — `angularTerm`,
 * `applyImpulse`, `supportPoints`, `tangentBasis` and `pointKinematicsInto` are
 * imported from there rather than reimplemented. Read that file's header first;
 * everything it says about accumulate-and-clamp, the fixed friction basis and
 * why a contact is a support-set PATCH and not a point applies here unchanged.
 *
 * The two-body differences, and only these:
 *
 *   1. EFFECTIVE INVERSE MASS is `2 + angularTerm(a) + angularTerm(b)` instead
 *      of `1 + angularTerm`. Both bodies move, and CAR_MASS is a single constant
 *      shared by every body in ./car-config, so mass still divides out of
 *      everything and the impulses stay per-unit-mass.
 *   2. DEPENETRATION IS SPLIT: each car moves half the overlap depth along the
 *      minimum-translation axis. Equal masses, equal shares.
 *   3. THE PATCH IS THE SPARSER OF THE TWO SUPPORT SETS. Against a triangle the
 *      car is the only box and its support set is the manifold. Between two
 *      boxes there are two candidate sets, and the correct manifold is the
 *      sharper feature: a corner poking into a flat side is a ONE-point contact,
 *      and solving it at the flat side's four points would refuse to let the
 *      poking car rotate. Taking `min(sizeA, sizeB)` picks the corner in that
 *      case, the edge in an edge-face case, and the face when both are faces.
 *      [UNVERIFIED-ESTIMATE: this is a manifold heuristic, not a sourced rule.
 *       Bullet clips the incident face against the reference face and would
 *       produce fewer, differently placed points for a partially overlapping
 *       face-face contact, and would produce ONE point for edge-edge where this
 *       produces two. It is the same order of approximation ./car-arena already
 *       makes against the mesh.]
 *
 * Restitution is 0.1 and friction 0.09, both from ./constants and both fixed by
 * RocketSim rather than combined from the bodies. Cars are slippery and nearly
 * dead: the visible pop when two cars meet is the scripted bump, not the
 * contact.
 *
 * ===========================================================================
 * ORDER WITHIN THE TICK
 * ===========================================================================
 *
 *   1. cooldown timers down.
 *   2. bump/demolition decisions, read from the state as it stands BEFORE any
 *      response — RocketSim's callback runs during collision detection, ahead of
 *      the solver, so `state.vel` there is the pre-solve velocity.
 *   3. the rigid-body response.
 *   4. the bump impulses, added last. That is RocketSim's
 *      `_velocityImpulseCache`, which is applied after the constraint solve, and
 *      the ordering matters: a bump added before the solver would be partly
 *      cancelled by the very contact that caused it.
 *
 * A bump is a SCRIPTED delta-v on the victim alone. The attacker keeps its
 * velocity; there is no momentum exchange in it, and looking for one is the
 * classic misreading of what the curves are.
 */

import {
  angularTerm,
  applyImpulse,
  pointKinematicsInto,
  supportPoints,
  tangentBasis,
} from './car-arena';
import { carInertiaPerMass } from './car-config';
import type { CarState } from './car-state';
import {
  BUMP_COOLDOWN_TIME,
  BUMP_MIN_FORWARD_DIST,
  BUMP_UPWARD_VEL_CURVE,
  BUMP_VEL_AIR_CURVE,
  BUMP_VEL_GROUND_CURVE,
  CAR_MAX_ANGULAR_SPEED,
  CAR_MAX_SPEED,
  CARCAR_COLLISION_FRICTION,
  CARCAR_COLLISION_RESTITUTION,
} from './constants';
import {
  approachRates,
  contactPointLocal,
  demoQualifies,
  demolishCar,
  type DemoEvent,
} from './demo';
import { clampLengthBy, col, piecewiseLinear, UP, vec3, type Vec3 } from './math';
import {
  carHitboxInto,
  makeCarHitbox,
  makeOBBOverlap,
  obbOverlap,
  type OBB,
  type OBBOverlap,
} from './obb';

/** Maximum support points per contact: a face. Mirrors ./car-arena. */
const PATCH_MAX = 4;

/** Gauss-Seidel sweeps per pass. The same count and the same reasoning as
 *  ./car-arena: eight is where the residual stops moving. Unlike that file, one
 *  sweep here covers EVERY pair, so the multi-body coupling a pile-up produces
 *  is resolved inside a single pass rather than across passes. */
const SOLVER_ITERATIONS = 8;

/**
 * Impulse passes per tick.
 *
 * The second one is a backstop rather than the workhorse: the position
 * relaxation below normally leaves nothing overlapping, so pass 2 queries, finds
 * nothing and exits. It earns its keep only when the relaxation runs out of
 * sweeps with cars still wedged, which is the case where those cars also need
 * another velocity solve.
 */
const CONTACT_PASSES = 2;

/**
 * Extra POSITION-ONLY relaxation sweeps at the end of each pass.
 *
 * Depenetrating one pair moves a car into whatever else it is touching, and in
 * a genuine pile-up — three cars wedged, or six arriving at one point — a single
 * split correction leaves several uu of overlap that the next tick inherits.
 * These sweeps re-query and push again until nothing overlaps, and they are
 * separate from CONTACT_PASSES because they are nearly free: a SAT over the
 * pairs and two vector adds, with none of the eight-sweep impulse solve behind
 * them. The loop exits on the first clean query, so an ordinary two-car contact
 * pays for exactly one of them.
 */
const DEPENETRATION_SWEEPS = 8;

/**
 * Below this overlap a pair is treated as already resolved. It is also the
 * residual an exact depenetration leaves behind: pushing two boxes apart by
 * their own overlap lands them on a shared face to within a few ulps of the
 * coordinates involved, which for arena-scale positions is around 1e-14 uu.
 * Without the floor, a pair would be re-solved every tick forever on a contact
 * that is numerically zero.
 */
const DEPTH_EPSILON = 1e-6;

/** Same accumulated-float slack as ./demo uses on the respawn timer: a cooldown
 *  stepped 30 times by 1/120 lands a few ulps either side of zero, and a car
 *  that has waited the full BUMP_COOLDOWN_TIME must not be made to wait one more
 *  tick because of it. */
const TIME_EPSILON = 1e-9;

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

/** One bump applied this tick. The impulse is the victim's whole delta-v. */
export interface BumpEvent {
  attacker: CarState;
  victim: CarState;
  /** Indices into the car list handed to `stepCarCarCollisions`. */
  attackerIndex: number;
  victimIndex: number;
  /** The curves' input: the attacker's speed along the line to the victim. */
  speedTowards: number;
  /** Delta-v applied to the VICTIM, uu/s. The attacker loses nothing. */
  impulse: Vec3;
}

export interface CarCarEvents {
  demolitions: DemoEvent[];
  bumps: BumpEvent[];
  /**
   * Overlapping pairs found this tick, BEFORE any response — "how many pairs of
   * cars were touching". Most of them are neither a bump nor a demolition (a
   * side scrape is just a contact), so it is not derivable from the two lists
   * above, and it is what a renderer or a test wants in order to know that two
   * cars met at all.
   */
  contacts: number;
}

export interface CarCarOptions {
  /** RocketSim's `MutatorConfig::enableTeamDemos`. Off in the default game. */
  teamDemos?: boolean;
  /**
   * RocketSim's `MutatorConfig::bumpForceScale`, default 1. Scales ONLY the
   * upward term of the bump — see the header.
   */
  bumpForceScale?: number;
}

// ---------------------------------------------------------------------------
// Scratch
// ---------------------------------------------------------------------------

/** Per-world working state, allocated once. Nothing here survives a tick, so it
 *  is neither cloned nor serialized. */
export interface CarCarScratch {
  /** One hitbox per car, rebuilt every pass. */
  boxes: OBB[];
  /** The SAT's minimum-translation result, reused per pair. */
  overlap: OBBOverlap;
  /** Car indices per contact, flattened as (a, b). */
  pairs: Int32Array;
  /** Contact normal per contact, out of `a` toward `b`, flattened as triples. */
  normals: Float64Array;
  /** Overlap depth per contact. */
  depths: Float64Array;
  /** Patch points per contact, flattened as PATCH_MAX (x, y, z) triples. */
  points: Float64Array;
  patchSize: Int32Array;
  accN: Float64Array;
  accT1: Float64Array;
  accT2: Float64Array;
  bias: Float64Array;
  kn: Float64Array;
  k1: Float64Array;
  k2: Float64Array;
  /** Friction basis per contact, flattened as (t1, t2) triples. */
  tangents: Float64Array;
  /** How many contacts the buffers hold: every unordered pair. */
  maxContacts: number;
}

export const makeCarCarScratch = (carCount: number): CarCarScratch => {
  const n = Math.max(carCount, 0);
  const maxContacts = Math.max((n * (n - 1)) / 2, 1);
  const slots = maxContacts * PATCH_MAX;
  return {
    boxes: Array.from({ length: n }, makeCarHitbox),
    overlap: makeOBBOverlap(),
    pairs: new Int32Array(maxContacts * 2),
    normals: new Float64Array(maxContacts * 3),
    depths: new Float64Array(maxContacts),
    points: new Float64Array(slots * 3),
    patchSize: new Int32Array(maxContacts),
    accN: new Float64Array(slots),
    accT1: new Float64Array(slots),
    accT2: new Float64Array(slots),
    bias: new Float64Array(slots),
    kn: new Float64Array(slots),
    k1: new Float64Array(slots),
    k2: new Float64Array(slots),
    tangents: new Float64Array(maxContacts * 6),
    maxContacts,
  };
};

// Two lever arms and two point velocities, one pair per body.
const _ra = vec3(0, 0, 0);
const _rb = vec3(0, 0, 0);
const _va = vec3(0, 0, 0);
const _vb = vec3(0, 0, 0);
const _axis = vec3(0, 0, 0);
const _patchA = new Float64Array(PATCH_MAX * 3);
const _patchB = new Float64Array(PATCH_MAX * 3);

// ---------------------------------------------------------------------------
// Contact generation
// ---------------------------------------------------------------------------

/**
 * Every overlapping live pair, at the cars' current pose. Pairs are visited in
 * (i < j) index order, so the contact list — and therefore the whole solve — is
 * order-deterministic.
 */
const buildContacts = (cars: readonly CarState[], s: CarCarScratch): number => {
  for (let i = 0; i < cars.length; i++) {
    const car = cars[i];
    if (car.demolished) continue;
    carHitboxInto(s.boxes[i], car.position, car.orientation, car.config);
  }

  let count = 0;
  for (let i = 0; i < cars.length && count < s.maxContacts; i++) {
    if (cars[i].demolished) continue;
    for (let j = i + 1; j < cars.length && count < s.maxContacts; j++) {
      if (cars[j].demolished) continue;
      if (!obbOverlap(s.boxes[i], s.boxes[j], s.overlap)) continue;
      if (s.overlap.depth <= DEPTH_EPSILON) continue;

      s.pairs[count * 2] = i;
      s.pairs[count * 2 + 1] = j;
      s.normals[count * 3] = s.overlap.normal.x;
      s.normals[count * 3 + 1] = s.overlap.normal.y;
      s.normals[count * 3 + 2] = s.overlap.normal.z;
      s.depths[count] = s.overlap.depth;
      count++;
    }
  }
  return count;
};

/**
 * The contact patch for one pair: the sparser of the two boxes' support sets
 * along the contact normal, written into the contact's point slots. See the
 * header for why the sparser one.
 */
const buildPatch = (s: CarCarScratch, c: number, boxA: OBB, boxB: OBB): number => {
  const nx = s.normals[c * 3];
  const ny = s.normals[c * 3 + 1];
  const nz = s.normals[c * 3 + 2];

  // `supportPoints` returns the vertices furthest along -axis. A's deepest
  // points into B are furthest along +n, so A gets -n and B gets +n.
  _axis.x = -nx;
  _axis.y = -ny;
  _axis.z = -nz;
  const sizeA = supportPoints(boxA.center, boxA.orientation, boxA.halfExtents, _axis, _patchA);
  _axis.x = nx;
  _axis.y = ny;
  _axis.z = nz;
  const sizeB = supportPoints(boxB.center, boxB.orientation, boxB.halfExtents, _axis, _patchB);

  const from = sizeB < sizeA ? _patchB : _patchA;
  const size = sizeB < sizeA ? sizeB : sizeA;
  const base = c * PATCH_MAX * 3;
  for (let k = 0; k < size * 3; k++) s.points[base + k] = from[k];
  s.patchSize[c] = size;
  return size;
};

// ---------------------------------------------------------------------------
// The solver
// ---------------------------------------------------------------------------

/** One Gauss-Seidel sweep over every point of every pair patch. */
const solveSweep = (cars: readonly CarState[], s: CarCarScratch, count: number): void => {
  for (let c = 0; c < count; c++) {
    const ai = s.pairs[c * 2];
    const bi = s.pairs[c * 2 + 1];
    const a = cars[ai];
    const b = cars[bi];
    // Memoized per config in ./car-config, and looked up once per contact
    // rather than once per point per sweep.
    const ia = carInertiaPerMass(a.config);
    const ib = carInertiaPerMass(b.config);

    const nx = s.normals[c * 3];
    const ny = s.normals[c * 3 + 1];
    const nz = s.normals[c * 3 + 2];
    const t1x = s.tangents[c * 6];
    const t1y = s.tangents[c * 6 + 1];
    const t1z = s.tangents[c * 6 + 2];
    const t2x = s.tangents[c * 6 + 3];
    const t2y = s.tangents[c * 6 + 4];
    const t2z = s.tangents[c * 6 + 5];

    for (let p = 0; p < s.patchSize[c]; p++) {
      const slot = c * PATCH_MAX + p;
      const px = s.points[slot * 3];
      const py = s.points[slot * 3 + 1];
      const pz = s.points[slot * 3 + 2];

      // --- normal
      pointKinematicsInto(a, px, py, pz, _ra, _va);
      pointKinematicsInto(b, px, py, pz, _rb, _vb);
      const vn = (_vb.x - _va.x) * nx + (_vb.y - _va.y) * ny + (_vb.z - _va.z) * nz;
      const wanted = (s.bias[slot] - vn) / s.kn[slot];
      const totalN = Math.max(0, s.accN[slot] + wanted);
      const dn = totalN - s.accN[slot];
      s.accN[slot] = totalN;
      if (dn !== 0) {
        applyImpulse(a, ia, _ra, -nx * dn, -ny * dn, -nz * dn);
        applyImpulse(b, ib, _rb, nx * dn, ny * dn, nz * dn);
      }

      if (totalN === 0) continue;

      // --- friction
      pointKinematicsInto(a, px, py, pz, _ra, _va);
      pointKinematicsInto(b, px, py, pz, _rb, _vb);
      const rvx = _vb.x - _va.x;
      const rvy = _vb.y - _va.y;
      const rvz = _vb.z - _va.z;
      const v1 = rvx * t1x + rvy * t1y + rvz * t1z;
      const v2 = rvx * t2x + rvy * t2y + rvz * t2z;

      let new1 = s.accT1[slot] - v1 / s.k1[slot];
      let new2 = s.accT2[slot] - v2 / s.k2[slot];
      const limit = CARCAR_COLLISION_FRICTION * totalN;
      const mag = Math.hypot(new1, new2);
      if (mag > limit) {
        const scale = limit / mag;
        new1 *= scale;
        new2 *= scale;
      }
      const d1 = new1 - s.accT1[slot];
      const d2 = new2 - s.accT2[slot];
      s.accT1[slot] = new1;
      s.accT2[slot] = new2;
      if (d1 !== 0 || d2 !== 0) {
        const jx = t1x * d1 + t2x * d2;
        const jy = t1y * d1 + t2y * d2;
        const jz = t1z * d1 + t2z * d2;
        applyImpulse(a, ia, _ra, -jx, -jy, -jz);
        applyImpulse(b, ib, _rb, jx, jy, jz);
      }
    }
  }
};

/** Push every overlapping pair apart along its minimum-translation axis, each
 *  car taking half. Positions only. */
const depenetrate = (cars: readonly CarState[], s: CarCarScratch, count: number): void => {
  for (let c = 0; c < count; c++) {
    const half = s.depths[c] / 2;
    const a = cars[s.pairs[c * 2]];
    const b = cars[s.pairs[c * 2 + 1]];
    a.position.x -= s.normals[c * 3] * half;
    a.position.y -= s.normals[c * 3 + 1] * half;
    a.position.z -= s.normals[c * 3 + 2] * half;
    b.position.x += s.normals[c * 3] * half;
    b.position.y += s.normals[c * 3 + 1] * half;
    b.position.z += s.normals[c * 3 + 2] * half;
  }
};

/**
 * Depenetrate and resolve every car-car overlap. Mutates positions, velocities
 * and angular velocities; returns the number of contacts found on the FIRST
 * pass, which is "how many pairs were touching this tick".
 */
export const resolveCarCarContacts = (
  cars: readonly CarState[],
  s: CarCarScratch
): number => {
  let firstPassCount = 0;
  for (let pass = 0; pass < CONTACT_PASSES; pass++) {
    const count = buildContacts(cars, s);
    if (pass === 0) firstPassCount = count;
    if (count === 0) break;

    // Split depenetration, all pairs first, so the patches are built at the
    // resolved pose rather than at an intermediate one.
    depenetrate(cars, s, count);

    // Rebuild the boxes at the depenetrated pose, then the patches and the
    // per-point constants. The restitution target is measured ONCE here, before
    // any impulse: re-reading it inside the sweeps would treat one collision as
    // eight fresh ones and pump energy into both cars.
    for (let i = 0; i < cars.length; i++) {
      const car = cars[i];
      if (car.demolished) continue;
      carHitboxInto(s.boxes[i], car.position, car.orientation, car.config);
    }

    for (let c = 0; c < count; c++) {
      const ai = s.pairs[c * 2];
      const bi = s.pairs[c * 2 + 1];
      const a = cars[ai];
      const b = cars[bi];
      const ia = carInertiaPerMass(a.config);
      const ib = carInertiaPerMass(b.config);

      const size = buildPatch(s, c, s.boxes[ai], s.boxes[bi]);
      _axis.x = s.normals[c * 3];
      _axis.y = s.normals[c * 3 + 1];
      _axis.z = s.normals[c * 3 + 2];
      tangentBasis(_axis, s.tangents, c * 6);
      const t = s.tangents;
      const to = c * 6;

      for (let p = 0; p < size; p++) {
        const slot = c * PATCH_MAX + p;
        const px = s.points[slot * 3];
        const py = s.points[slot * 3 + 1];
        const pz = s.points[slot * 3 + 2];
        pointKinematicsInto(a, px, py, pz, _ra, _va);
        pointKinematicsInto(b, px, py, pz, _rb, _vb);
        const vn =
          (_vb.x - _va.x) * _axis.x + (_vb.y - _va.y) * _axis.y + (_vb.z - _va.z) * _axis.z;
        s.bias[slot] = vn < 0 ? -CARCAR_COLLISION_RESTITUTION * vn : 0;

        // Two movable bodies of equal mass: 1/m + 1/m, per unit mass, is 2.
        s.kn[slot] =
          2 +
          angularTerm(a.orientation, ia, _ra, _axis.x, _axis.y, _axis.z) +
          angularTerm(b.orientation, ib, _rb, _axis.x, _axis.y, _axis.z);
        s.k1[slot] =
          2 +
          angularTerm(a.orientation, ia, _ra, t[to], t[to + 1], t[to + 2]) +
          angularTerm(b.orientation, ib, _rb, t[to], t[to + 1], t[to + 2]);
        s.k2[slot] =
          2 +
          angularTerm(a.orientation, ia, _ra, t[to + 3], t[to + 4], t[to + 5]) +
          angularTerm(b.orientation, ib, _rb, t[to + 3], t[to + 4], t[to + 5]);
        s.accN[slot] = 0;
        s.accT1[slot] = 0;
        s.accT2[slot] = 0;
      }
    }

    for (let iter = 0; iter < SOLVER_ITERATIONS; iter++) solveSweep(cars, s, count);

    for (let c = 0; c < count; c++) {
      for (let k = 0; k < 2; k++) {
        const car = cars[s.pairs[c * 2 + k]];
        clampLengthBy(car.velocity, CAR_MAX_SPEED);
        clampLengthBy(car.angularVelocity, CAR_MAX_ANGULAR_SPEED);
      }
    }

    // Position-only relaxation. It clobbers the contact list, which is why it
    // runs after the solve rather than before it.
    for (let sweep = 0; sweep < DEPENETRATION_SWEEPS; sweep++) {
      const remaining = buildContacts(cars, s);
      if (remaining === 0) break;
      depenetrate(cars, s, remaining);
    }
  }
  return firstPassCount;
};

// ---------------------------------------------------------------------------
// The bump
// ---------------------------------------------------------------------------

/**
 * The scripted delta-v a bump gives its VICTIM, exactly as the source composes
 * it:
 *
 *     velDir * (ground|air)Curve(speed) + hitUpDir * upwardCurve(speed) * scale
 *
 * The ground/air choice and the up direction both come from the VICTIM's ground
 * state, and `bumpForceScale` multiplies only the upward term. A grounded victim
 * is launched along its own up axis — so a car bumped on a wall goes off the
 * wall, not toward the ceiling — while an airborne one goes along world +Z.
 */
export const bumpDeltaV = (
  velDir: Vec3,
  victim: CarState,
  speedTowards: number,
  bumpForceScale = 1
): Vec3 => {
  const base = piecewiseLinear(
    victim.onGround ? BUMP_VEL_GROUND_CURVE : BUMP_VEL_AIR_CURVE,
    speedTowards
  );
  const upward = piecewiseLinear(BUMP_UPWARD_VEL_CURVE, speedTowards) * bumpForceScale;
  const hitUpDir = victim.onGround ? col(victim.orientation, 2) : UP;
  return vec3(
    velDir.x * base + hitUpDir.x * upward,
    velDir.y * base + hitUpDir.y * upward,
    velDir.z * base + hitUpDir.z * upward
  );
};

/**
 * The callback itself, run for every overlapping pair. Decisions only — the
 * bump impulses are returned rather than applied, because they belong after the
 * contact solve (see the header). Demolitions ARE applied here, because the
 * source applies them inline and the `return` that follows depends on it.
 */
const decideBumps = (
  cars: readonly CarState[],
  s: CarCarScratch,
  count: number,
  teamDemos: boolean,
  bumpForceScale: number,
  events: CarCarEvents
): void => {
  for (let c = 0; c < count; c++) {
    const i = s.pairs[c * 2];
    const j = s.pairs[c * 2 + 1];

    for (let dir = 0; dir < 2; dir++) {
      const attackerIndex = dir === 0 ? i : j;
      const victimIndex = dir === 0 ? j : i;
      const attacker = cars[attackerIndex];
      const victim = cars[victimIndex];

      // The source's `return`: a demolished car on either side abandons BOTH
      // directions of this contact, not just this one.
      if (attacker.demolished || victim.demolished) break;

      // Cooldown, keyed to this victim only.
      const contact = attacker.carContact;
      if (contact.otherCarIndex === victimIndex && contact.cooldownTimer > 0) continue;

      const rates = approachRates(attacker, victim);
      if (rates === null) continue;

      // The front-bumper band gates bumps and demos alike.
      if (contactPointLocal(attacker, victim).x <= BUMP_MIN_FORWARD_DIST) continue;

      if (demoQualifies(attacker, victim, teamDemos)) {
        demolishCar(victim);
        events.demolitions.push({ attacker, victim });
      } else {
        events.bumps.push({
          attacker,
          victim,
          attackerIndex,
          victimIndex,
          speedTowards: rates.speedTowards,
          impulse: bumpDeltaV(rates.velDir, victim, rates.speedTowards, bumpForceScale),
        });
      }

      contact.otherCarIndex = victimIndex;
      contact.cooldownTimer = BUMP_COOLDOWN_TIME;
    }
  }
};

/**
 * Count every car's bump cooldown down by one tick. Clamped at zero, and the
 * victim slot is released when it gets there so that the serialized state has
 * exactly one representation of "no cooldown".
 */
export const stepBumpCooldowns = (cars: readonly CarState[], dt: number): void => {
  for (const car of cars) {
    const contact = car.carContact;
    if (contact.cooldownTimer <= 0) continue;
    contact.cooldownTimer -= dt;
    if (contact.cooldownTimer <= TIME_EPSILON) {
      contact.cooldownTimer = 0;
      contact.otherCarIndex = -1;
    }
  }
};

/**
 * One tick of car-vs-car for the whole field: cooldowns, then bump and
 * demolition decisions on the pre-response state, then the rigid-body response,
 * then the bump impulses on top of it.
 *
 * `scratch` is a caller-owned buffer so the per-tick path allocates nothing; a
 * caller without one (a test) gets a fresh buffer, and one sized for a different
 * roster is replaced rather than overflowed.
 *
 * Respawn timers are NOT stepped here — `stepRespawns` in ./demo owns those, and
 * the world runs it first so that a car demolished on tick T is not charged for
 * the tick it died on.
 */
export const stepCarCarCollisions = (
  cars: readonly CarState[],
  dt: number,
  scratch?: CarCarScratch,
  options: CarCarOptions = {}
): CarCarEvents => {
  const s =
    scratch !== undefined && scratch.boxes.length >= cars.length
      ? scratch
      : makeCarCarScratch(cars.length);

  stepBumpCooldowns(cars, dt);

  const events: CarCarEvents = { demolitions: [], bumps: [], contacts: 0 };
  const count = buildContacts(cars, s);
  events.contacts = count;
  if (count > 0) {
    decideBumps(
      cars,
      s,
      count,
      options.teamDemos ?? false,
      options.bumpForceScale ?? 1,
      events
    );
  }

  resolveCarCarContacts(cars, s);

  // RocketSim's `_velocityImpulseCache`: applied after the constraint solve. A
  // victim demolished elsewhere in the same tick has been zeroed and must not be
  // handed velocity on the way out.
  for (const bump of events.bumps) {
    if (bump.victim.demolished) continue;
    bump.victim.velocity.x += bump.impulse.x;
    bump.victim.velocity.y += bump.impulse.y;
    bump.victim.velocity.z += bump.impulse.z;
    clampLengthBy(bump.victim.velocity, CAR_MAX_SPEED);
  }

  return events;
};
